import { useMutation } from "@tanstack/react-query";
import type { CreateTrialAccount } from "@/domain/usecase/CreateTrialAccount";
import type { CreateTrialAccountForm } from "./CreateTrialAccountSchema";
import {
  toCreateTrialAccountCommand,
  toCreateTrialAccountErrorMessage,
} from "./CreateTrialAccountMapper";

type UiState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "success" };

type SubmitResult = { type: "SUCCESS" } | { type: "FAILED" };

export function useCreateTrialAccountViewModel(
  createTrialAccount: CreateTrialAccount,
) {
  const mutation = useMutation({
    mutationFn: (form: CreateTrialAccountForm) =>
      createTrialAccount.execute(toCreateTrialAccountCommand(form)),
    retry: false,
    gcTime: 0,
  });

  const state: UiState = mutation.isPending
    ? { status: "loading" }
    : mutation.isError
      ? {
          status: "error",
          message: toCreateTrialAccountErrorMessage(mutation.error),
        }
      : mutation.isSuccess
        ? { status: "success" }
        : { status: "idle" };

  const submit = async (
    form: CreateTrialAccountForm,
  ): Promise<SubmitResult> => {
    try {
      await mutation.mutateAsync(form);
      return { type: "SUCCESS" };
    } catch {
      return { type: "FAILED" };
    }
  };

  return { state, intents: { submit } };
}
